import React, { useState } from 'react';
import { Row, Col } from 'antd';
import { CheckOutlined, RightOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';

import TimeTrackingGridWizardDateStep from './TimeTrackingGridWizardDateStep';
import TimeTrackingGridWizardCostcodeStep from './TimeTrackingGridWizardCostcodeStep';
import TimeTrackingGridWizardUserEquipmentStep from './TimeTrackingGridWizardUserEquipmentStep';
import TimeTrackingGridWizardPreview from './TimeTrackingGridWizardPreview';
import { buildWizardColumns, buildWizardRows } from './buildWizardGridPayload';

const STEPS = [
  {
    title: 'Select Date',
    component: ({ selectedDate, setSelectedDate, goToNextStep }) => (
      <TimeTrackingGridWizardDateStep
        selectedDate={selectedDate}
        setSelectedDate={setSelectedDate}
        goToNextStep={goToNextStep}
      />
    ),
  },
  {
    title: 'Add Cost Codes',
    component: ({
      goToNextStep,
      divisions,
      configProps,
      selectedCostcodes,
      setSelectedCostcodes,
      costcodes,
      fetchProjectCostcodes,
    }) => (
      <TimeTrackingGridWizardCostcodeStep
        goToNextStep={goToNextStep}
        divisions={divisions}
        configProps={configProps}
        selectedCostcodes={selectedCostcodes}
        setSelectedCostcodes={setSelectedCostcodes}
        costcodes={costcodes}
        fetchProjectCostcodes={fetchProjectCostcodes}
      />
    ),
  },
  {
    title: 'Add Users & Equipment',
    component: ({
      goToNextStep,
      selectedUsers,
      setSelectedUsers,
      selectedEquipment,
      setSelectedEquipment,
      divisions,
    }) => (
      <TimeTrackingGridWizardUserEquipmentStep
        goToNextStep={goToNextStep}
        selectedUsers={selectedUsers}
        setSelectedUsers={setSelectedUsers}
        selectedEquipment={selectedEquipment}
        setSelectedEquipment={setSelectedEquipment}
        divisions={divisions}
      />
    ),
  },
];

export default function TimeTrackingGridWizard({
  id,
  divisions,
  configProps,
  setResponses,
  costcodes,
  fetchProjectCostcodes,
}) {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedDate, setSelectedDate] = useState(DateTime.local().toISODate());
  const [selectedCostcodes, setSelectedCostcodes] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);

  const getStepClassName = (baseClassName, index) => {
    if (index === currentStep) {
      return `${baseClassName} active`;
    }
    if (index < currentStep) {
      return `${baseClassName} done`;
    }
    return `${baseClassName} next`;
  };

  const onDateChange = (newDateMoment) => {
    const timezone = DateTime.local().zoneName;
    const newDate = newDateMoment
      ? DateTime.fromJSDate(newDateMoment.toDate(), { zone: timezone }).toISODate()
      : null;

    setSelectedDate(newDate);
  };

  const applyWizardToResponses = () => {
    if (!setResponses || !id) return;

    const columns = buildWizardColumns(selectedCostcodes);
    const columnKeys = columns.map((c) => c.rowId);
    const values = buildWizardRows(selectedUsers, selectedEquipment, columnKeys);
    const timezone = DateTime.local().zoneName;

    setResponses((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        columns,
        values,
        date: selectedDate,
        timezone,
      },
    }));
  };

  const goToStep = (step) => {
    if (currentStep <= step) return;
    setCurrentStep(step);
  };

  const goToNextStep = () => {
    const newStep = currentStep + 1;
    if (newStep === STEPS.length) {
      applyWizardToResponses();
    } else {
      setCurrentStep(newStep);
    }
  };

  return (
    <div className="time-tracking-grid-wizard-container">
      <div className="time-tracking-grid-wizard-header">
        {STEPS.map((step, index) => (
          <>
            <div
              key={step.title}
                className={getStepClassName('time-tracking-grid-wizard-step', index)}
                onClick={() => goToStep(index)}
            >
              <div className={getStepClassName('time-tracking-grid-wizard-step-number', index)}>
                {index <= currentStep ? <CheckOutlined className="time-tracking-grid-wizard-step-number-icon" /> : index + 1}
              </div>
              {step.title}
            </div>
            { (index < STEPS.length - 1) && <RightOutlined className="time-tracking-grid-wizard-step-arrow" /> }
          </>
        ))}
      </div>
      <div className="time-tracking-grid-wizard-content">
        <Row>
          <Col span={12} className="time-tracking-grid-wizard-step-container">
            {STEPS[currentStep].component({
              selectedDate,
              setSelectedDate: onDateChange,
              goToNextStep,
              selectedCostcodes,
              setSelectedCostcodes,
              selectedUsers,
              setSelectedUsers,
              selectedEquipment,
              setSelectedEquipment,
              divisions,
              configProps,
              costcodes,
              fetchProjectCostcodes,
            })}
          </Col>
          <Col span={12}>
            <TimeTrackingGridWizardPreview
              selectedDate={selectedDate}
              selectedCostcodes={selectedCostcodes}
              selectedUsers={selectedUsers}
              selectedEquipment={selectedEquipment}
              currentStep={currentStep}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
}

TimeTrackingGridWizard.propTypes = {
  id: PropTypes.string.isRequired,
  divisions: PropTypes.arrayOf(PropTypes.string).isRequired,
  configProps: PropTypes.shape({}).isRequired,
  setResponses: PropTypes.func.isRequired,
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  fetchProjectCostcodes: PropTypes.func,
};

TimeTrackingGridWizard.defaultProps = {
  costcodes: [],
  fetchProjectCostcodes: null,
};
